import {
  canManageAccessScope,
  canViewAcceptanceNav,
  getCapabilitiesFromUser,
  hasCapability,
  hasLiveOpsNavAccess,
  hasSetupReadAccess,
  isExternalSafetyReviewer,
  isProductEvaluator,
  type AccessUserLike,
} from '../../auth/access';
import {
  normalizeWarehouseProductProfile,
  type WarehouseProductProfile,
} from '../../utils/warehouseNavigation';

export type PlatformWorkflowStep =
  | 'configure'
  | 'scan'
  | 'review'
  | 'case'
  | 'action'
  | 'measure'
  | 'record';

export type PlatformPersonaId =
  | 'setup'
  | 'safety_reviewer'
  | 'safety_manager'
  | 'external_reviewer'
  | 'product_evaluator'
  | 'admin';

export type PlatformLayerDefinition = {
  id: string;
  label: string;
};

export type RoleWorkflowRowDefinition = {
  id: PlatformPersonaId;
  title: string;
  flow: string;
};

export const PLATFORM_ORIENTATION_TITLE = 'One platform. Role-specific safety workflows.';

export const PLATFORM_ORIENTATION_BODY =
  'Edgentik covers Overview, Investigate, Safety Cases, and Configure. Overview summarizes what needs care; Investigate owns evidence; Safety Cases is the workspace of record for attention, case, scan, and record workflows. RBAC keeps each user focused on the workflow they are responsible for.';

/** Top-level product lanes — matches warehouse sidebar section labels. */
export const PLATFORM_LAYERS: PlatformLayerDefinition[] = [
  { id: 'configure', label: 'Configure' },
  { id: 'review', label: 'Investigate' },
  { id: 'safety_intelligence', label: 'Overview' },
  { id: 'safety_reports', label: 'Safety Cases' },
];

export const PLATFORM_LAYERS_HELPER =
  'Safety Cases unifies the case, scan, and record surfaces into one action workspace. RBAC narrows that into role-specific workflows.';

export const PLATFORM_WORKFLOW_STRIP_LABELS: Record<PlatformWorkflowStep, string> = {
  configure: 'Configure',
  measure: 'Measure',
  case: 'Safety Case',
  scan: 'Safety Scan',
  review: 'Review & Sign',
  action: 'Control',
  record: 'Follow-up',
};

/** Product order: activation creates the Safety Case first; Scans attach afterward. */
export const PLATFORM_WORKFLOW_STRIP_STEPS: PlatformWorkflowStep[] = [
  'configure',
  'measure',
  'case',
  'scan',
  'review',
  'action',
  'record',
];

/**
 * Role rows use product persona labels (Live Ops / Acceptance Manager)
 * while keeping stable internal persona ids for RBAC mapping.
 */
export const ROLE_WORKFLOW_ROWS: RoleWorkflowRowDefinition[] = [
  {
    id: 'setup',
    title: 'Setup / Deployment',
    flow: 'Bring Up → Cameras → Zones → Policies → Preflight → Deploy → Confirm Healthy',
  },
  {
    id: 'safety_reviewer',
    title: 'Live Ops',
    flow: 'Incident review → Inspect evidence → Assign Action → Track Impact',
  },
  {
    id: 'safety_manager',
    title: 'Acceptance Manager',
    flow: 'Safety Case → Safety Scan → Sign Safety Scan → Safety Control',
  },
  {
    id: 'external_reviewer',
    title: 'External Reviewer',
    flow: 'View Signed Safety Scan → Review Evidence Package',
  },
];

function profileToPersona(profile: WarehouseProductProfile | null): PlatformPersonaId {
  if (profile === 'setup') return 'setup';
  if (profile === 'acceptance') return 'safety_manager';
  if (profile === 'admin') return 'admin';
  return 'safety_reviewer';
}

function capabilityFallbackPersona(user: AccessUserLike): PlatformPersonaId {
  const capabilities = getCapabilitiesFromUser(user);
  const hasAdminAccess = hasCapability(capabilities, 'admin:*');
  const hasSetup = hasSetupReadAccess(capabilities);
  const hasLiveOps = hasLiveOpsNavAccess(capabilities);
  const hasAcceptance = canViewAcceptanceNav(capabilities);

  if (hasSetup && !hasLiveOps && !hasAcceptance) {
    return 'setup';
  }
  if (hasAcceptance && !hasLiveOps) {
    return 'safety_manager';
  }
  if (hasLiveOps) {
    return 'safety_reviewer';
  }
  if (hasAdminAccess || canManageAccessScope(capabilities)) {
    return 'admin';
  }
  return 'safety_reviewer';
}

export function resolvePlatformPersona(user: AccessUserLike): PlatformPersonaId {
  if (isExternalSafetyReviewer(user)) return 'external_reviewer';
  if (isProductEvaluator(user)) return 'product_evaluator';

  const declared = normalizeWarehouseProductProfile(user?.primary_profile);
  if (declared) return profileToPersona(declared);

  return capabilityFallbackPersona(user);
}

/** Which role row to highlight, or null when none fits (admin / hybrid evaluator). */
export function resolveHighlightedRoleRow(persona: PlatformPersonaId): PlatformPersonaId | null {
  if (persona === 'admin' || persona === 'product_evaluator') return null;
  return persona;
}

export function visibleRoleWorkflowRows(persona: PlatformPersonaId): RoleWorkflowRowDefinition[] {
  if (persona === 'external_reviewer') {
    return ROLE_WORKFLOW_ROWS.filter((row) => row.id === 'external_reviewer');
  }
  if (persona === 'product_evaluator') {
    // Hybrid evaluator nav spans Live Ops + Configure + Safety Reports — show those rows, no false highlight.
    return ROLE_WORKFLOW_ROWS.filter((row) => row.id !== 'external_reviewer');
  }
  if (persona === 'admin') {
    // Admin owns Users / Access Scope, not an operational workflow row.
    return ROLE_WORKFLOW_ROWS;
  }
  return ROLE_WORKFLOW_ROWS;
}

export const ADMIN_ORIENTATION_NOTE =
  'Your Admin role focuses on Users and Access Scope. The workflows below are for operational and acceptance roles.';

export const PRODUCT_EVALUATOR_ORIENTATION_NOTE =
  'Product evaluation spans Camera View, Incident review, configuration, and Safety Reports — not a single role lane.';

/** Shared Investigate page heading. Canonical Investigate surface; Pattern remains the queue object noun. */
export const RISK_PATTERN_EVIDENCE_REVIEW_HEADING = 'Investigate';
export const INVESTIGATE_PAGE_SUBTITLE =
  'Review recurring activity, inspect evidence, and decide what happens next.';
